import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Grid, Divider, Header, Form, Checkbox } from 'semantic-ui-react'
import CO2Service, { CO2OptionsType } from '~/api/services/co2'
import { Nullable } from '~/types'
import { CO2El } from '../../../style'
import { StyledGridColumns, StyledGridRow } from '~/pages/Dairy/MilkFixedPrice/ShowMilkFixedPrice/style'
import { StyledGrid, CustomRow, StyledSegment, HeaderField, StyledOtherMilkWrapper } from '../style'
import MilkProductIcon from '@assets/svgs/milk-products.svg'
import MilkFatProteinIcon from '@assets/svgs/milk-fat-protein.svg'
import { FormFields } from '~/components/FormFields'
import { useFormContext, useWatch } from 'react-hook-form'
import EuroCoin from '@assets/svgs/euro-coin.svg'
import AdressBookIcon from '@assets/svgs/contact-book.svg'
import ProfitIcon from '@assets/svgs/profit.svg'
import { formatNumber, handleErrors, splitEvery } from '~/utils/helpers'
import InfoPopup from '~/components/InfoPopup'
import TableComponent from '~/components/TableComponent'
import PreviewModal from './PreviewModal'
import { useParams } from 'react-router-dom'

interface Props {
  options: Nullable<CO2OptionsType>
  setOptions: (options: CO2OptionsType) => void
}

const CharacteristicsOfProd: React.FC<Props> = (props) => {
  const { options, setOptions } = props
  const { control, setValue, getValues } = useFormContext()
  const { id, farmerId } = useParams()
  const { t } = useTranslation()
  const billingData = [
    {
      label: t('Milk sold (in kg)'),
      icon: MilkProductIcon,
      key: 'totalMilkAmount',
      min: 1000,
      max: 100000000,
      suffix: ' kg'
    },
    {
      label: t('Milk price net including surcharges (in € cents/kg)'),
      icon: ProfitIcon,
      decimalScale: 2,
      min: 20,
      max: 90,
      key: 'milkPrice',
      suffix: ' € Ct/kg'
    },
    {
      label: t('Protein content of the milk sold (in%)'),
      key: 'protein',
      decimalScale: 2,
      min: 2,
      max: 6,
      icon: MilkFatProteinIcon,
      suffix: ' %'
    },
    {
      label: t('Fat content of the milk sold (in%)'),
      icon: MilkFatProteinIcon,
      key: 'fat',
      min: 3,
      max: 7,
      decimalScale: 2,
      suffix: ' %'
    }
  ]

  const [isPreviewModal, setIsPreviewModal] = useState(false)

  const watchSellOtherMilkSelect = useWatch({ control, name: 'sellOtherMilkSelected' })

  useEffect(() => {
    if (getValues('otherMilkAmount')) {
      setValue('sellOtherMilkSelected', true)
    }
    if (options.filePreview) {
      setIsPreviewModal(true)
    }
  }, [])

  useEffect(() => {
    if (!watchSellOtherMilkSelect) {
      setValue('otherMilkAmount', '')
    }
  }, [watchSellOtherMilkSelect])

  function renderFarmerInfo() {
    return <HeaderField>
      <Header className='header' style={{ marginBottom: 5 }}>
        <img style={{ width: '2.5em' }} src={AdressBookIcon} />
        {t('Dairy master data')}
      </Header>
      <CO2El.StyledDivider />
      <StyledSegment>
        <StyledGrid columns={2} stackable>
          <Divider vertical style={{ margin: 0 }} />
          <Grid.Column>
            <Grid columns={2} stackable>
              <CustomRow>
                <Grid.Column>{t('Supplier number')}:</Grid.Column>
                <StyledGridColumns>{options?.farmer?.supplierNo || '-'}</StyledGridColumns>
              </CustomRow>
              <StyledGridRow>
                <Grid.Column>{t('Firm name')}:</Grid.Column>
                <StyledGridColumns>{options?.farmer?.companyName || '-'}</StyledGridColumns>
              </StyledGridRow>
              <StyledGridRow>
                <Grid.Column>{t('Taxation')}:</Grid.Column>
                <StyledGridColumns>{options?.farmer?.saleTaxLabel || '-'}</StyledGridColumns>
              </StyledGridRow>
            </Grid>
          </Grid.Column>
          <Grid.Column style={{ paddingBottom: '1em' }}>
            <Grid columns={2} stackable style={{ marginLeft: 5 }}>
              <StyledGridRow>
                <Grid.Column className='left-column'>{t('Street & house number')}:</Grid.Column>
                <StyledGridColumns>{`${options?.farmer?.streetName || '-'}, ${options?.farmer?.streetNumber || '-'}`}</StyledGridColumns>
              </StyledGridRow>
              <StyledGridRow>
                <Grid.Column className='left-column'>{t('Postal code & City')}:</Grid.Column>
                <StyledGridColumns>{`${options?.farmer?.zip || '-'} ${options?.farmer?.city || '-'}`}</StyledGridColumns>
              </StyledGridRow>
              <StyledGridRow>
                <Grid.Column className='left-column'>{t('Federal state (Country)')}:</Grid.Column>
                <StyledGridColumns>{`${options.farmer?.stateName || '-'} (${options?.farmer?.label || '-'})`}</StyledGridColumns>
              </StyledGridRow>
            </Grid>
          </Grid.Column>
        </StyledGrid>
      </StyledSegment>
    </HeaderField>
  }

  function renderInputs() {
    return <CO2El.BillingWrapper>
      <Form.Group widths={'equal'} style={{ width: '100%' }}>
        {billingData.map(billing => {
          const hideInput = options?.useMilkPrice === 0 && billing.key === 'milkPrice'
          return !hideInput && <FormFields.Input
            name={billing.key}
            key={billing.key}
            typeInput={'controled'}
            allowDecimals={Boolean(billing?.decimalScale)}
            noErrorMessage
            withoutAsterisk
            decimalScale={billing?.decimalScale}
            control={control}
            suffix={billing.suffix}
            placeholder={billing.label}
            styleControlled={{ width: options?.useMilkPrice === 0 ? 300 : 200, marginLeft: 45 }}
            label={<div className='billing'>
              <img style={{ width: '2.5em' }} src={billing.icon} />
              <div className='column'>
                <div>{billing.label}</div>
              </div>
            </div>}
            rules={{
              required: true,
              validate: (value) => {
                const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
                if (transformValue < billing.min || transformValue > billing.max) {
                  return false
                }
                return true
              }
            }}
          />
        })}
      </Form.Group>
    </CO2El.BillingWrapper>
  }

  function renderDairyDataTables(tableColumnHeader) {
    const dateColumn = {
      name: t('Month'),
      key: 'month',
      type: 'date',
      format: 'MMM YYYY'
    }
    const amountsSplit = splitEvery(options?.farmer?.amounts, 4)
    return <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      {amountsSplit.map(amountSplit => {
        return <TableComponent
          key={`amountSplit-${Math.random()}`}
          tableColumnHeaders={[dateColumn, tableColumnHeader]}
          rows={amountSplit}
          total={amountSplit?.length}
          removePagination
        />
      })}
    </div>
  }

  function displayAsLabels() {
    return <HeaderField>
      <StyledSegment>
        <StyledGrid columns={2} stackable>
          <Divider vertical style={{ margin: 0 }} />
          <Grid.Column>
            <Grid columns={2} stackable>
              <CustomRow>
                <Grid.Column>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <img style={{ width: '2em', marginRight: 10 }} src={MilkProductIcon} />
                    <div className='column'>
                      <div>{t('Milk sold (in kg)')}:</div>
                    </div>
                  </div>
                </Grid.Column>
                <StyledGridColumns style={{ marginTop: 10, display: 'flex' }}>
                  {`${formatNumber(options?.farmer?.totalMilkAmount, 0)} kg` || '-'}
                  <InfoPopup
                    content={renderDairyDataTables({ name: t('Milk sold'), key: 'amount', type: 'number-whole', amountUnit: 'kg' })}
                    styleIcon={{ fontSize: '1.3em', marginLeft: 5 }}
                    position={'top center'}
                  />
                </StyledGridColumns>
              </CustomRow>
              {options?.useMilkPrice === 1 && <CustomRow>
                <Grid.Column>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <img style={{ width: '2em', marginRight: 10 }} src={ProfitIcon} />
                    <div className='column'>
                      <div>{t('Milk price net including surcharges (in € cents/kg)')}:</div>
                    </div>
                  </div>
                </Grid.Column>
                <StyledGridColumns style={{ marginTop: 5, display: 'flex' }}>
                  {`${formatNumber(options?.farmer?.milkPrice, 2)} € Ct/kg` || '-'}
                  <InfoPopup
                    content={renderDairyDataTables({
                      name: t('Net milk price including surcharges'),
                      key: 'netPrice',
                      type: 'value-with-unit',
                      decimalScale: 2,
                      amountUnit: '€ Ct/kg'
                    })}
                    styleIcon={{ fontSize: '1.3em', marginLeft: 5 }}
                    position={'top center'}
                  />
                </StyledGridColumns>
              </CustomRow> }
            </Grid>
          </Grid.Column>
          <Grid.Column>
            <Grid columns={2} stackable style={{ marginLeft: 5 }}>
              <CustomRow>
                <Grid.Column>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <img style={{ width: '2em', marginRight: 10 }} src={MilkFatProteinIcon} />
                    <div className='column'>
                      <div>{t('Protein content of the milk sold (in%)')}:</div>
                    </div>
                  </div>
                </Grid.Column>
                <StyledGridColumns style={{ marginTop: 5, display: 'flex' }}>
                  {`${formatNumber(options?.farmer?.protein, 2)} %` || '-'}
                  <InfoPopup
                    content={renderDairyDataTables({
                      name: t('Protein content of milk sold'),
                      key: 'protein',
                      type: 'value-with-unit',
                      decimalScale: 2,
                      amountUnit: '%'
                    })}
                    styleIcon={{ fontSize: '1.3em', marginLeft: 5 }}
                    position={'top center'}
                  />
                </StyledGridColumns>
              </CustomRow>
              <CustomRow>
                <Grid.Column>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <img style={{ width: '2em', marginRight: 10 }} src={MilkFatProteinIcon} />
                    <div className='column'>
                      <div>{t('Fat content of the milk sold (in%)')}:</div>
                    </div>
                  </div></Grid.Column>
                <StyledGridColumns style={{ marginTop: 5, display: 'flex' }}>
                  {`${formatNumber(options?.farmer?.fat, 2)} %` || '-'}
                  <InfoPopup
                    content={renderDairyDataTables({
                      name: t('Fat content of milk sold'),
                      key: 'fat',
                      type: 'value-with-unit',
                      decimalScale: 2,
                      amountUnit: '%'
                    })}
                    styleIcon={{ fontSize: '1.3em', marginLeft: 5 }}
                    position={'top center'}
                  />
                </StyledGridColumns>
              </CustomRow>
            </Grid>
          </Grid.Column>
        </StyledGrid>
      </StyledSegment>
    </HeaderField >
  }

  function renderBillingData() {
    return <HeaderField>
      <Header className='header' style={{ marginBottom: 5, display: 'flex', alignItems: 'center', gap: 10 }}>
        <img style={{ width: '2.5em' }} src={EuroCoin} />
        {t('Milk billing data')}
        <InfoPopup
          content={t('This data was pre-filled by the dairy and corresponds to the actual data for the survey year.')}
          styleIcon={{ fontSize: '1.3em' }}
          position={'top center'}
        />
      </Header>
      <CO2El.StyledDivider />
      {options?.farmer?.totalMilkAmount ? displayAsLabels() : renderInputs()}
      <StyledOtherMilkWrapper>
        <Checkbox
          label={<label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div>{t('If necessary, enter the amount of milk from your direct marketing here')}</div>
            <InfoPopup
              content={t('The calculator needs this information for correct milk production and to calculate the allocation for the GHG result.')}
              styleIcon={{ fontSize: '1.2em' }}
              position={'top center'}
            />
          </label>}
          checked={watchSellOtherMilkSelect}
          onChange={(_, data) => setValue('sellOtherMilkSelected', data.checked)}
        />
        <FormFields.Input
          disabled={!watchSellOtherMilkSelect}
          control={control}
          withoutAsterisk
          name='otherMilkAmount'
          placeholder={'in kg'}
          suffix={' kg'}
          styleControlled={{ width: 200, marginLeft: 25 }}
          typeInput={'controled'}
          allowDecimals={false}
          noErrorMessage
          rules={{
            required: watchSellOtherMilkSelect
          }}
        />
      </StyledOtherMilkWrapper>
    </HeaderField>
  }

  async function handleNoShowChecked() {
    try {
      await CO2Service.noShowCheckedSave(id, { farmerId })
    } catch (error) {
      handleErrors(error)
    }
  }

  function renderPreviewModal() {
    return <PreviewModal
      isOpen={isPreviewModal}
      handleClose={(isNoShowChecked) => {
        setIsPreviewModal(false)
        setOptions({
          ...options,
          filePreview: !isNoShowChecked
        })
        if (isNoShowChecked) {
          handleNoShowChecked()
        }
      }}
    />
  }

  return <>
    {renderFarmerInfo()}
    {renderBillingData()}
    {renderPreviewModal()}
  </>
}
export default CharacteristicsOfProd